import { useRef, useState } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import PopupHeader from '../PopupHeader/PopupHeader'
import IconButton from 'mixtiles-web-common/ui/IconButton'
import { XIcon } from '../../icons'
import TextButton, { VARIANTS } from 'mixtiles-web-common/ui/TextButton'
import { translateManager as t } from '../../services/TranslateManager'
import * as S from './StripePaymentForm.styles'
import PaymentManager from '../../services/PaymentManager'
import { logger } from '../../services/logger'
import Checkbox from 'mixtiles-web-common/ui/Checkbox'
import StripeAddressForm from '../StripeAddressForm/StripeAddressForm'
import { convertAddressToStripeFormat } from '../StripeAddressForm/stripeAdapter'
import { getUserEmail } from '../../services/userEmail'
import ErrorMessage from '../ErrorMessage/ErrorMessage'
import * as EmailValidator from 'email-validator'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { useProductType } from '../../services/ProductTypesManager'
import { experimentManager } from '../../services/ExperimentManager/ExperimentManager'
import {
  ExternalToPaymentMethod,
  isExternalPaymentMethod,
  PAYMENT_METHOD_ORDER,
} from './StripePaymentForm.utils'
import {
  type PaymentMethodResult,
  type StripeError,
  type StripePaymentElementChangeEvent,
  type StripePaymentElementOptions,
} from '@stripe/stripe-js'
import { type Address } from '../Pricing/types/checkout.types'
import { PaymentMethodType } from '../../services/PaymentManager.types'
import { isPaymentMethodGoogleOrApple } from '../../stores/paymentStore/payment.utils'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import PreviewSpinner, {
  SPINNER_SIZES,
} from 'components/Spinners/PreviewSpinner'
import {
  validateAddress,
  validateFullName,
  validateAddressLatinCharacters,
  shouldCapturePhone,
  getNameKanaError,
  NAME_KANA_ERROR_MESSAGE_KEYS,
} from '../../stores/addressStore/address.utils'
import { useTiles } from '../../hooks/useTiles'
import AddressValidationErrorDialog from 'components/AddressValidationErrorDialog/AddressValidationErrorDialog'
import { LANGUAGES } from '../../locale/Translation.consts'
import { useLanguage } from '../../services/LangProvider'

type Props = {
  onDone: ({
    paymentMethodType,
  }: {
    paymentMethodType: PaymentMethodType
  }) => void
  onClose: () => void
  shippingAddress: Address
  onSubmitShippingAddress: (address: Address, email?: string) => void
  shouldRenderPaymentElement?: boolean
}

/**
 * A form that collects payment details using stripe's PaymentElement.
 * If users already entered a shipping address (through "Add Address" popup), it
 * will also be used as a billing address, but they can set a different billing address.
 * If a user entered a billing address, they can choose to set it as a shipping address.
 */
function StripePaymentForm({
  onDone,
  onClose,
  shippingAddress,
  onSubmitShippingAddress,
  shouldRenderPaymentElement = true,
}: Props) {
  const { productType } = useProductType()
  const stripe = useStripe()
  const elements = useElements()
  const [isPaymentElementLoaded, setIsPaymentElementLoaded] =
    useState<boolean>(false)
  const [isComplete, setIsComplete] = useState<boolean>(false)
  const [formErrorMessage, setFormErrorMessage] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const hasShippingAddress = !!shippingAddress
  const [isSameAddress, setIsSameAddress] =
    useState<boolean>(hasShippingAddress) // Is same shipping and billing address
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<
    string | null
  >(null)
  const selectedPaymentMethod = selectedPaymentMethodType
    ? ExternalToPaymentMethod[selectedPaymentMethodType]
    : undefined
  const isWalletSelectedInExpressVariant =
    !!selectedPaymentMethod &&
    isPaymentMethodGoogleOrApple(selectedPaymentMethod) &&
    experimentManager.isEnabled('express-checkout-payment-only')
  const shouldCollectBillingAddress =
    (!hasShippingAddress || (hasShippingAddress && !isSameAddress)) &&
    !isWalletSelectedInExpressVariant
  const [billingAddress, setBillingAddress] = useState<Address | null>(null)
  const [isBillingAddressComplete, setIsBillingAddressComplete] =
    useState(false)
  const [fullNameKanaInvalid, setFullNameKanaInvalid] = useState<boolean>(false)
  const language = useLanguage()
  const [email, setEmail] = useState<{
    value?: string
    valid?: boolean
  }>(() => {
    const initialEmail = getUserEmail()
    return initialEmail
      ? { value: initialEmail, valid: EmailValidator.validate(initialEmail) }
      : {}
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState<boolean>(false)
  const [validationError, setValidationError] = useState<string>('')
  const [allowUseAsIsButton, setAllowUseAsIsButton] = useState<boolean>(true)
  const { tiles } = useTiles()

  const showFormErrorMessage = (message: string) => {
    setFormErrorMessage(message)
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const shouldCapturePhoneForShipping = (): boolean => {
    if (!billingAddress?.countryId) return false
    try {
      return shouldCapturePhone({
        countryId: billingAddress.countryId,
        tiles,
        phoneNumber: billingAddress?.phoneNumber,
      })
    } catch {
      return true
    }
  }
  const paymentMethodOrder =
    experimentManager.getVariantPayload('payment-order-bnpl') ||
    PAYMENT_METHOD_ORDER

  const options: StripePaymentElementOptions = {
    fields: {
      billingDetails: 'never',
    },
    wallets: {
      applePay: 'auto',
      googlePay: 'auto',
    },
    paymentMethodOrder,
  }

  const handleChange = (event: StripePaymentElementChangeEvent) => {
    const { complete } = event
    setIsComplete(complete)
    setSelectedPaymentMethodType(event.value.type)
  }

  // Return whether is valid and error message in case it's not valid
  const validateForm = () => {
    let errorMessage = ''
    if (!isComplete) {
      errorMessage = t.get(
        'general.payment_form.validation_errors.missing_fields.general'
      )
    } else if (shouldCollectBillingAddress) {
      // If has a different billing address
      if (!email.valid) {
        errorMessage = t.get(
          'general.address_form.validation_errors.email.invalid'
        )
      }
      if (!isBillingAddressComplete) {
        errorMessage = t.get(
          'general.payment_form.validation_errors.address.missing'
        )
      }
    }
    return { isValid: !errorMessage, errorMessage }
  }

  const _createPaymentMethod = async (
    billingAddress: Address
  ): Promise<PaymentMethodResult> => {
    const { name, address, phone } = convertAddressToStripeFormat(
      billingAddress || {}
    )
    return stripe!.createPaymentMethod({
      elements: elements!,
      params: {
        billing_details: {
          email: email.value,
          address,
          phone,
          name,
        },
      },
    })
  }

  const _handleAddPaymentMethodFail = (error: any) => {
    logger.warning('Error adding payment method', error)
    const message =
      error.type === 'card_error' || error.type === 'StripeCardError'
        ? `${t.get(
            'order.checkout.payment_errors.card_declined_text_1'
          )}\n${t.get('order.checkout.payment_errors.card_declined_text_2')}`
        : t.get('general.errors.general_title')
    showFormErrorMessage(message)
  }

  const _onDone = async (
    paymentMethodType: PaymentMethodType,
    shippingAddress: Address
  ) => {
    // Save address if the user wanted the same billing address as shipping address
    if (
      !hasShippingAddress &&
      (isSameAddress || productType === PRODUCT_TYPES.GIFT_CARD)
    ) {
      onSubmitShippingAddress(
        shippingAddress,
        email.valid ? email.value : undefined
      )
    }
    onDone({ paymentMethodType })
    setIsSubmitting(false)
  }

  // Validates the billing address when it will be used as shipping address
  const validateBillingAddressForShipping = async (
    address: Address
  ): Promise<boolean> => {
    // Local validations (matching AddressForm behavior)
    const allowJapanese = language === LANGUAGES.JAPANESE
    if (!validateFullName(address.fullName || '', { allowJapanese })) {
      showFormErrorMessage(
        t.get('general.address_form.validation_errors.full_name.invalid')
      )
      return false
    }

    const kanaError = allowJapanese
      ? getNameKanaError(address.fullName, address.fullNameKana)
      : null
    if (kanaError) {
      showFormErrorMessage(t.get(NAME_KANA_ERROR_MESSAGE_KEYS[kanaError]))
      setFullNameKanaInvalid(true)
      return false
    }

    if (!validateAddressLatinCharacters(address)) {
      showFormErrorMessage(
        t.get('general.address_form.validation_errors.non_latin_characters')
      )
      return false
    }

    if (shouldCapturePhoneForShipping() && !address.phoneNumber) {
      showFormErrorMessage(
        t.get(
          'general.address_form.validation_errors.missing_fields.specific',
          {
            v1: t.get('general.address_form.form_fields.phone').toLowerCase(),
          }
        )
      )
      return false
    }

    // Backend validation
    try {
      const { isValid, allowEditAddress, validationErrors } =
        await validateAddress(address)
      if (!isValid) {
        setShowValidationErrorDialog(true)
        setAllowUseAsIsButton(allowEditAddress)
        setValidationError(validationErrors.join('. '))
        return false
      }
      return true
    } catch (error) {
      logger.warning('Address validation backend error, skipping validation', {
        error,
      })
      analytics.track('Address Validation Backend Error', { Error: error })
      // Continue the order flow without indicating an error to the user.
      return true
    }
  }

  const handleSubmitClick = async (skipAddressValidation = false) => {
    setIsSubmitting(true)

    // Create a payment method with the billing (or shipping) address
    const billingAddressToSend =
      isSameAddress && hasShippingAddress ? shippingAddress : billingAddress

    // If billing address will be used as shipping address, validate it (including PO box check)
    const willUseBillingAsShipping =
      !hasShippingAddress &&
      isSameAddress &&
      billingAddress &&
      productType !== PRODUCT_TYPES.GIFT_CARD
    if (willUseBillingAsShipping && !skipAddressValidation) {
      const isAddressValid =
        await validateBillingAddressForShipping(billingAddress)
      if (!isAddressValid) {
        setIsSubmitting(false)
        return
      }
    }

    // Validate form fields
    const { isValid, errorMessage } = validateForm()
    if (!isValid) {
      showFormErrorMessage(errorMessage)
      setIsSubmitting(false)
      return
    }

    if (
      selectedPaymentMethodType &&
      isExternalPaymentMethod(selectedPaymentMethodType)
    ) {
      _onDone(
        ExternalToPaymentMethod[selectedPaymentMethodType],
        billingAddressToSend
      ).catch((error) => {
        _handleAddPaymentMethodFail(error)
        setIsSubmitting(false)
      })
      return
    }
    const { error, paymentMethod } =
      await _createPaymentMethod(billingAddressToSend)
    if (error) {
      // This point is only reached if there's an immediate error when confirming the payment
      showFormErrorMessage(error?.message || '')
      setIsSubmitting(false)
      return
    }

    const paymentMethodType =
      paymentMethod.type === 'card'
        ? PaymentMethodType.CREDIT_CARD
        : (paymentMethod.type as PaymentMethodType) // Mixtiles save paymentMethodType as "creditCard" instead of "card" for some reason
    PaymentManager.addStripePaymentMethod(
      paymentMethod,
      email.value,
      paymentMethodType
    )
      .then(() => {
        _onDone(paymentMethodType, billingAddressToSend)
      })
      .catch((error) => {
        _handleAddPaymentMethodFail(error)
        setIsSubmitting(false)
      })
  }

  const handleLoadError = (event: {
    elementType: 'payment'
    error: StripeError
  }) => {
    const { error } = event
    logger.error('Failed loading stripe PaymentElement', error)
    analytics.track(EVENT_NAMES.PAYMENT_ELEMENT_LOAD_ERROR, {
      errorType: error.type,
      errorCode: error.code,
      errorMessage: error.message,
    })
  }

  const handleIsSameAddressChange = (isChecked: boolean) => {
    setIsSameAddress(isChecked)
    analytics.track(EVENT_NAMES.SAME_ADDRESS_CHECKBOX_TAPPED, {
      'Is Checked': isChecked,
      'Has Shipping Address': hasShippingAddress,
    })
  }

  const handleAddressChange = (
    address: Address,
    isAddressComplete: boolean
  ) => {
    // Stripe's AddressElement doesn't carry fullNameKana — preserve it across changes.
    setBillingAddress((prev: Address | null) => ({
      ...address,
      fullNameKana: prev?.fullNameKana,
    }))
    setIsBillingAddressComplete(isAddressComplete)
  }

  const handleEmailChange = (newEmail: string) => {
    setEmail({ value: newEmail, valid: EmailValidator.validate(newEmail) })
  }

  const SameAddressCheckbox = isPaymentElementLoaded && (
    <S.IsSameAddressContainer>
      <Checkbox checked={isSameAddress} onChange={handleIsSameAddressChange} />
      <S.IsSameAddressText>
        {t.get('general.payment_form.same_shipping_address')}
      </S.IsSameAddressText>
    </S.IsSameAddressContainer>
  )

  const handlePaymentElementReady = () => {
    setIsPaymentElementLoaded(true)
    analytics.track(EVENT_NAMES.PAYMENT_ELEMENT_LOADED)
  }

  return (
    <S.Container>
      <PopupHeader
        title={t.get('order.checkout.payment_methods')}
        leftButton={
          <div data-testid="payment-dialog-close-button">
            <IconButton onClick={onClose}>
              <XIcon />
            </IconButton>
          </div>
        }
        rightButton={
          <TextButton
            variant={VARIANTS.primary}
            loading={isSubmitting}
            onClick={() => handleSubmitClick(false)}
            testId="add-credit-card-done-button"
          >
            {t.get('general.done')}
          </TextButton>
        }
      />
      <S.Content
        ref={containerRef}
        data-testid={
          isPaymentElementLoaded ? 'stripe-payment-element-loaded' : null
        }
      >
        {formErrorMessage && <ErrorMessage message={formErrorMessage} />}
        {shouldRenderPaymentElement ? (
          <PaymentElement
            options={options}
            onChange={(e) => handleChange(e)}
            onLoadError={handleLoadError}
            onReady={handlePaymentElementReady}
            className="stripe-payment-element"
          />
        ) : (
          <S.SpinnerContainer>
            <PreviewSpinner size={SPINNER_SIZES.medium} />
          </S.SpinnerContainer>
        )}
        {hasShippingAddress && SameAddressCheckbox}
        {shouldCollectBillingAddress && (
          <>
            <S.Divider />
            {isPaymentElementLoaded && !hasShippingAddress && (
              <S.BillingAddressHeader>
                {t.get('general.payment_form.header')}
              </S.BillingAddressHeader>
            )}
            <StripeAddressForm
              mode="billing"
              address={billingAddress}
              onChange={handleAddressChange}
              shouldCapturePhone={
                !hasShippingAddress &&
                isSameAddress &&
                shouldCapturePhoneForShipping()
              }
              emailInputParams={{
                value: email.value || '',
                onChange: handleEmailChange,
                isInvalid: !!(email.value && !email.valid),
                onBlur: () => {},
              }}
              fullNameKanaParams={{
                show:
                  language === LANGUAGES.JAPANESE &&
                  !hasShippingAddress &&
                  productType !== PRODUCT_TYPES.GIFT_CARD,
                value: billingAddress?.fullNameKana || '',
                onChange: (newValue) => {
                  setBillingAddress((prev: Address | null) => ({
                    ...prev,
                    fullNameKana: newValue,
                  }))
                  setFullNameKanaInvalid(false)
                  setFormErrorMessage('')
                },
                isInvalid: fullNameKanaInvalid,
              }}
            />
          </>
        )}
        {!hasShippingAddress &&
          billingAddress?.city?.length > 0 &&
          SameAddressCheckbox}
      </S.Content>
      <AddressValidationErrorDialog
        open={showValidationErrorDialog}
        address={billingAddress}
        validationError={validationError}
        allowUseAsIs={allowUseAsIsButton}
        onUseAsIs={() => {
          setShowValidationErrorDialog(false)
          handleSubmitClick(true)
        }}
        onEditAddress={() => setShowValidationErrorDialog(false)}
      />
    </S.Container>
  )
}

export default StripePaymentForm
